import { useT, type Uebersetzer } from "../i18n";
import type { Maschinentyp, Variante } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Die Ausstattung einer Miete — „Kran MKG 401 mit Anschläger".

   **Bis zum 16.09.2026 gab es keinen Weg dorthin** (12-offene-fragen.md,
   P28). Die API nahm `variante_id` an, die Maske schickte keine. Beim
   Anhänger blieb der Satz „mit Baumaschine" damit unerreichbar, und beim
   Kran — der nur Sätze je Ausstattung trägt — fand die Preisfindung ohne
   Wahl gar keinen (`preis.variante_noetig`).

   **Vorbelegt, nicht gefragt** (Regel 11): die Ausstattung, die im Stamm
   als Standard markiert ist; hat der Typ einen Grundpreis und keinen
   Standard, der Grundpreis. Der Kran steht damit auf „ohne Anschläger",
   der Anhänger auf 40,00 — so, wie sie auf dem Papier ganz oben stehen.
   ══════════════════════════════════════════════════════════════════ */

/** Was ein Typ an Ausstattung anbietet, und was vorbelegt ist. `""` = Grundpreis. */
export function ausstattungVon(typ: Maschinentyp | undefined): {
  gibt: boolean;
  grundpreis: boolean;
  vorgabe: string;
} {
  if (!typ || typ.varianten.length === 0) return { gibt: false, grundpreis: true, vorgabe: "" };
  const grundpreis = typ.wirksam.tarife.some((x) => x.variante_id === null);
  const standard = typ.varianten.find((v) => v.standard);
  return {
    gibt: true,
    grundpreis,
    vorgabe: standard?.id ?? (grundpreis ? "" : (typ.varianten[0]?.id ?? "")),
  };
}

/**
 * Der Name der Position mit Ausstattung — „Kran MKG 401 · mit Anschläger".
 *
 * Er steht so auf dem Beleg in Lexware: Die Position trägt ihre Bezeichnung
 * selbst, und ein Kran für 105 und einer für 120 dürfen dort nicht gleich
 * heißen.
 */
export function mitAusstattung(name: string, typ: Maschinentyp | undefined, varianteId: string): string {
  const v = typ?.varianten.find((x) => x.id === varianteId);
  return v ? `${name} · ${v.label}` : name;
}

/**
 * Was eine Ausstattung an Menschen bedeutet — ein Satz, gebaut aus den Daten
 * und nicht aus dem Namen.
 *
 * **Die Bedienungspflicht des Typs liest mit** (Durchgang 16.09.2026, PR3).
 * Bis dahin zählte nur `requires_staff` der Ausstattung, und unter „ohne
 * Bedienung" am Kran stand *„Ohne Personal — der Kunde fährt selbst."* Der
 * Kran geht nie ohne Fahrer hinaus (`nur_mit_person`); was die Ausstattung
 * ändert, ist nur, ob **derselbe** Fahrer auch anschlägt — Lisa: „ohne
 * Anschläger" heißt, der Kunde hängt selbst ein, „mit Anschläger" heißt,
 * der Fahrer schlägt an, „mit Arbeitskorb" heißt, der Korb hängt am Kran.
 * Keine davon bringt eine zweite Person (Migration 0103).
 *
 * Wer mitfährt, steht mit seiner Qualifikation da („Kranführerschein"), weil
 * die Daten keinen Berufsnamen tragen — erfunden wird keiner.
 */
export function ausstattungSatz(typ: Maschinentyp, v: Variante, t: Uebersetzer): string {
  const teile: string[] = [];
  const liste = (namen: string[]) => new Intl.ListFormat("de", { type: "conjunction" }).format(namen);

  if (typ.bedienung.pflicht === "nur_mit_person") {
    const scheine = typ.bedienung.qualifikationen.map((q) => q.label);
    teile.push(scheine.length
      ? t("preise.bedienungPflicht.mitSchein", { scheine: liste(scheine) })
      : t("preise.bedienungPflicht.ohneSchein"));
    // Eine Ausstattung, die trotzdem einen weiteren Menschen verlangt, sagt es.
    if (v.mit_personal > 0) {
      teile.push(v.qualifikation_label
        ? t("preise.bedienungPflicht.dazuRolle", { zahl: v.mit_personal, rolle: v.qualifikation_label })
        : t("preise.bedienungPflicht.dazu", { zahl: v.mit_personal }));
    }
  } else if (v.mit_personal <= 0) {
    teile.push(t("preise.bedienungOhne"));
  } else {
    teile.push(v.qualifikation_label
      ? t("preise.bedienungMitRolle", { zahl: v.mit_personal, rolle: v.qualifikation_label })
      : t("preise.bedienungMit", { zahl: v.mit_personal }));
  }
  if (v.zubehoer.length) teile.push(t("preise.bedienungZubehoer", { namen: liste(v.zubehoer) }));
  return teile.join(` ${t("allgemein.trennpunkt")} `);
}

/** Die Wahl selbst — nur da, wo der Typ Ausstattungen hat. */
export function AusstattungWahl({ typ, wert, aendern, id, className }: {
  typ: Maschinentyp | undefined;
  wert: string;
  aendern: (varianteId: string) => void;
  id?: string;
  className?: string;
}) {
  const t = useT();
  const art = ausstattungVon(typ);
  if (!typ || !art.gibt) return null;
  return (
    <select
      id={id}
      className={className ?? "feld"}
      value={wert}
      aria-label={t("positionen.ausstattung")}
      title={t("positionen.ausstattung")}
      onChange={(e) => aendern(e.target.value)}
    >
      {art.grundpreis ? <option value="">{t("positionen.ausstattungGrund")}</option> : null}
      {typ.varianten.map((v) => (
        <option key={v.id} value={v.id}>{v.label}</option>
      ))}
    </select>
  );
}
